import React, { useRef } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaToast, { ToastType } from "../../../../ui-kit/kuika-toast/kuika-toast";
import { failureMessageOr, IToastAction, ToastId, toastService } from "../../../../ui-kit/kuika-toast/toast-service";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

const TYPES: ToastType[] = ["success", "warning", "error", "info", "loading", "neutral"];

const BURST: string[] = ["Screen saved", "Datasource connected", "Action renamed", "Theme applied", "Branch created"];

const settleAfter = (ms: number, succeeds: boolean): Promise<number> =>
  new Promise<number>((resolve: (value: number) => void, reject: (reason: Error) => void): void => {
    window.setTimeout((): void => (succeeds ? resolve(1284) : reject(new Error("The export timed out"))), ms);
  });

/** A slow export that stops (rejects) when its signal aborts. */
const exportUntilAborted = (ms: number, signal: AbortSignal): Promise<number> =>
  new Promise<number>((resolve: (value: number) => void, reject: (reason: Error) => void): void => {
    const timer: number = window.setTimeout((): void => resolve(1284), ms);
    signal.addEventListener("abort", (): void => {
      window.clearTimeout(timer);
      reject(new Error("The export was cancelled"));
    });
  });

export const ToastStory: React.FC = (): JSX.Element => {
  const lastIdRef: React.MutableRefObject<ToastId | null> = useRef<ToastId | null>(null);

  const showBurst = (): void => {
    BURST.forEach((message: string): void => {
      lastIdRef.current = toastService.success(message);
    });
  };

  const showPromise = (succeeds: boolean): void => {
    lastIdRef.current = toastService.promise(settleAfter(1800, succeeds), {
      loading: "Exporting rows…",
      success: (rows: number): string => `Exported ${rows.toLocaleString("en-US")} rows`,
      error: failureMessageOr("The export failed")
    });
  };

  /** `neutral` toasts report a change that already happened and offer to reverse it — no icon, no
   *  title, just the message and its Undo button (`toastService.neutralWithAction`). */
  const showNeutralUndo = (): void => {
    const action: IToastAction = {
      label: "Undo",
      onClick: (): void => {
        toastService.success("Button1 restored");
      }
    };
    lastIdRef.current = toastService.neutralWithAction("Button1 deleted", action);
  };

  const showCancellablePromise = (): void => {
    const controller: AbortController = new AbortController();
    lastIdRef.current = toastService.promise(
      exportUntilAborted(6000, controller.signal),
      {
        loading: "Exporting rows…",
        success: (rows: number): string => `Exported ${rows.toLocaleString("en-US")} rows`,
        error: failureMessageOr("The export failed")
      },
      { action: { label: "Cancel", onClick: (): void => controller.abort() } }
    );
  };

  const updateLast = (): void => {
    if (lastIdRef.current !== null) toastService.update(lastIdRef.current, { message: "Updated in place" });
  };

  const dismissLast = (): void => {
    if (lastIdRef.current !== null) toastService.dismiss(lastIdRef.current);
  };

  return (
    <ShowcaseFrame
      title="Toast"
      componentName="KuikaToast"
      sourcePath="ui-kit/kuika-toast"
      description="Status notification sent through toastService. Every call returns an id to update or dismiss that toast; at most three show at once, newest in front, and the stack opens on hover."
    >
      <SpecRow
        label="Live stack"
        caption="five at once → three show, two wait; hover opens the stack and pauses time; a promise toast turns into its result in the same card; its Cancel (options.action) stops the work and closes it; a neutral toast is a bare Undo line with no icon or title"
      >
        <KuikaButton type="primary" size="sm" onClick={showBurst}>
          Send five
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={(): void => showPromise(true)}>
          Promise · resolves
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={(): void => showPromise(false)}>
          Promise · rejects
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={showCancellablePromise}>
          Promise · Cancel
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={showNeutralUndo}>
          Neutral · Undo
        </KuikaButton>
        <KuikaButton type="tertiary" size="sm" onClick={updateLast}>
          Update last
        </KuikaButton>
        <KuikaButton type="tertiary" size="sm" onClick={dismissLast}>
          Dismiss last
        </KuikaButton>
      </SpecRow>
      <VariantGrid
        label="type"
        cols={TYPES}
        cellMinWidth={300}
        render={(type: string): React.ReactNode => (
          <div style={{ width: "100%" }}>
            <KuikaToast
              type={type as ToastType}
              message={type === "neutral" ? "Button1 deleted" : `A ${type} occurred while building your screen.`}
              onClose={noop}
            />
          </div>
        )}
      />
      <SpecRow label="Custom title" caption="title overrides the default heading" align="stretch">
        <div style={{ width: 420 }}>
          <KuikaToast
            type="success"
            title="Deployment ready"
            message="The new build is now live on production."
            onClose={noop}
          />
        </div>
      </SpecRow>
      <SpecRow label="Long message" caption="copy-to-clipboard button appears" align="stretch">
        <div style={{ width: 480 }}>
          <KuikaToast
            type="error"
            title="Build failed"
            message="ts(2322): Type 'string | undefined' is not assignable to type 'string'. The toast surface keeps the full message in a copyable form for engineers."
            onClose={noop}
          />
        </div>
      </SpecRow>
      <SpecRow label="Without close" caption="onClose omitted">
        <div style={{ width: 360 }}>
          <KuikaToast type="warning" message="Heads up — staying online to keep your edits saved." />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
